import { useContext } from 'react';
import {
    FormaliteApi,
    DossierApi,
    DossierOpenSearchApi,
    CollectionDeDossierApi,
    FichiersDossierApi,
    FormalisteApi,
    StatutApi,
    PrestationApi,
    TapesDeTraitementDesDossiersApi,
    MessageApi,
    TypeDeDocumentDesDossiersApi,
    TypeDeDocumentApi,
    ObjectGroupsApi,
    PrestationArticlesApi,
    AcomptesApi,
    ReglementsApi,
    ProcdureDeValidationDesDossiersApi,
    ValideursDesDossiersApi,
    NotesApi,
    GreffeApi,
    CollectionOpenSearchApi,
    TachesEtRappelsDesDossierApi,
    ActionLoggerApi,
    DossiersDeSignatureLectroniqueApi,
    DocumentGedApi,
    FormaliteArticleOpenSearchApi,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import {
    CollectionOpenSearch,
    DossierFormalite,
    DossierFormaliteApiObject,
    DossierObjectGroup,
    DossierOpenSearch,
    DossierOpenSearchPrescriber,
    DossierValidator,
    type FormaliteDocument,
    FormalityModel,
} from '@europrocurement/l2d-domain/reducers';
import { FormalityReducerType } from '@europrocurement/l2d-domain/reducers/formalites/types';
import { DataSourcesThunksType, getDataThunkType } from '@europrocurement/l2d-redux-utils';

import { type DossierTaches } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import L2DApiContext from '../L2DApiContext';

export const MESSAGE_FROM_PRESCRIBER = 1;
export const MESSAGE_FROM_FORMALIST = 2;

export const useFormaliteService = () => {
    const context = useContext(L2DApiContext);
    if (!context) {
        throw new Error('useL2DApi must be used within a L2DApiProvider');
    }

    if (!context.formalite) {
        throw new Error('Formality service is not registred in L2DApiProvider');
    }
    const { apis, slices, thunks, fetchItems, getItems, models, reducer } = context.formalite;

    return {
        formaliteApi: apis.formaliteApi as FormaliteApi,
        formaliteOpenSearchApi: apis.formaliteOpenSearchApi as FormaliteArticleOpenSearchApi,
        dossierApi: apis.dossierApi as DossierApi,
        prestationArticlesApi: apis.prestationArticlesApi as PrestationArticlesApi,
        dossierOpenSearchApi: apis.dossierOpenSearchApi as DossierOpenSearchApi,
        collectionApi: apis.collectionApi as CollectionDeDossierApi,
        collectionOpenSearchApi: apis.collectionOpenSearchApi as CollectionOpenSearchApi,
        fichiersDossierApi: apis.fichiersDossierApi as FichiersDossierApi,
        formalisteApi: apis.formalisteApi as FormalisteApi,
        dossierStepApi: apis.dossierStepApi as TapesDeTraitementDesDossiersApi,
        statusApi: apis.statusApi as StatutApi,
        prestationFormaliteApi: apis.prestationFormaliteApi as PrestationApi,
        messageApi: apis.messageApi as MessageApi,
        documentsApi: apis.documentsApi as TypeDeDocumentDesDossiersApi,
        typeDocApi: apis.typeDocApi as TypeDeDocumentApi,
        dossierObjectGroupApi: apis.dossierObjectGroupApi as ObjectGroupsApi,
        acomptesApi: apis.acomptesApi as AcomptesApi,
        reglementsApi: apis.reglementsApi as ReglementsApi,
        validatorsApi: apis.validatorsApi as ValideursDesDossiersApi,
        validationProcessApi: apis.validationProcessApi as ProcdureDeValidationDesDossiersApi,
        notesApi: apis.notesApi as NotesApi,
        greffeApi: apis.greffeApi as GreffeApi,
        dossierValidatorApi: apis.dossierValidatorApi as ValideursDesDossiersApi,
        tachesEtRappelsDossierApi: apis.tachesEtRappelsDossierApi as TachesEtRappelsDesDossierApi,
        ActionLoggerApi: apis.actionLoggerApi as ActionLoggerApi,
        dossierSignApi: apis.dossierSignApi as DossiersDeSignatureLectroniqueApi,
        documentGedApi: apis.documentGedApi as DocumentGedApi,

        dossierFormaliteSlice: slices.dossierFormaliteSlice,
        dossierOpenSearchSlice: slices.dossierOpenSearchSlice,
        dossierOpenSearchPrescriberSlice: slices.dossierOpenSearchPrescriberSlice,
        formaliteSlice: slices.formaliteSlice,
        legalStatusSlice: slices.legalStatusSlice,
        categoryFormaliteSlice: slices.categoryFormaliteSlice,
        formaliteDocumentSlice: slices.formaliteDocumentSlice,
        formalisteSlice: slices.formalisteSlice,
        dossierStepSlice: slices.dossierStepSlice,
        dossierObjectGroupSlice: slices.dossierObjectGroupSlice,
        statusSlice: slices.statusSlice,
        dossierValidatorSlice: slices.dossierValidatorSlice,

        dossierFormaliteDataSourcesThunks:
            thunks.dossierFormaliteDataSourcesThunks as DataSourcesThunksType<DossierFormaliteApiObject>,
        dossierOpenSearchDataSourcesThunk: thunks.dossierFormaliteDataSourcesThunks,
        formaliteDataSourcesThunks: thunks.formaliteDataSourcesThunks,
        categoryFormaliteDataSourcesThunks: thunks.categoryFormaliteDataSourcesThunks,
        legalStatusDataSourcesThunks: thunks.legalStatusDataSourcesThunks,
        formaliteDocumentDataSourcesThunks: thunks.formaliteDocumentDataSourcesThunks,
        formalisteDataSourcesThunks: thunks.formalisteDataSourcesThunks,
        dossierObjectGroupDataSourcesThunks: thunks.dossierObjectGroupDataSourcesThunks,
        dossierStepDataSourcesThunks: thunks.dossierStepDataSourcesThunks,
        statusDataSourcesThunks: thunks.statusDataSourcesThunks,
        dossierValidatorDataSourcesThunks: thunks.dossierValidatorDataSourcesThunks,

        getFormalites: fetchItems.getFormalites,
        getDossierFormalite: fetchItems.getDossierFormalite as getDataThunkType<DossierFormalite>,
        getOpenSearchDossiers:
            fetchItems.getOpenSearchDossiers as getDataThunkType<DossierOpenSearch>,
        getOpenSearchDossiersPrescriber:
            fetchItems.getOpenSearchDossiersPrescriber as getDataThunkType<DossierOpenSearchPrescriber>,
        getOpenSearchCollections:
            fetchItems.getOpenSearchCollections as getDataThunkType<CollectionOpenSearch>,
        getAutocompleteDossierFormalite:
            fetchItems.getAutocompleteDossierFormalite as getDataThunkType<DossierFormalite>,
        getFormalistes: fetchItems.getFormalistes,
        getDossierSteps: fetchItems.getDossierSteps,
        getDossierObjectGroups: fetchItems.getObjectGroups as getDataThunkType<DossierObjectGroup>,
        getStatuses: fetchItems.getStatuses,
        getDossierOpenSearchObjectGroups:
            fetchItems.getDossierOpenSearchObjectGroups as getDataThunkType<DossierObjectGroup>,
        getDossierValidators: fetchItems.getDossierValidators as getDataThunkType<DossierValidator>,
        getDossierTaches: fetchItems.getDossierTaches as getDataThunkType<DossierTaches>,
        getFormaliteDocuments:
            fetchItems.getFormaliteDocuments as getDataThunkType<FormaliteDocument>,
        getActionLogDossiers: fetchItems.getActionLogDossiers,
        selectOrder: getItems.selectOrder,
        selectDossierFormalite: getItems.selectDossierFormalite,
        selectOpenSearchDossier: getItems.selectOpenSearchDossier,
        selectOpenSearchDossierPrescriber: getItems.selectOpenSearchDossierPrescriber,
        selectFormality: getItems.selectFormality,
        selectAutocompleteDossierFormalite: getItems.selectAutocompleteDossierFormalite,
        selectFormaliste: getItems.selectFormaliste,
        selectDossierStep: getItems.selectDossierStep,
        selectDossierObjectGroup: getItems.selectDossierObjectGroup,
        selectStatus: getItems.selectStatus,
        selectDossierValidator: getItems.selectDossierValidator,

        formalityModel: models.formality as FormalityModel,
        reducer: reducer as FormalityReducerType,
    };
};
